import * as React from 'react';
import { useMemo, useState, useEffect } from 'react';
import { Employee, Absence, Contract } from './hooks/useEmployeeData';
import EmployeeAvatar from './components/EmployeeAvatar';
import { TimeTracking } from './utils/timeTrackingUtils';
import { RangeMode, computeRange } from './utils/timeRangeUtils';
import dateUtil from './utils/dateUtil';

type DisplayMode = 'timeTracking' | 'absences' | 'availability' | 'simple' | 'shifts';

interface EmployeeSidebarProps {
  employees?: Employee[];
  activeEmployeeId?: number | null;
  setActiveEmployeeId?: (id: number | null) => void;
  activeEmployee?: Employee | null;
  setActiveEmployee?: (employee: Employee | null) => void;
  displayMode?: DisplayMode;

  // Time tracking props
  timeTrackings?: TimeTracking[];
  timeRangeMode?: RangeMode;
  customStart?: string;
  customEnd?: string;
  plannedHoursMap?: Record<number, number>;

  // Absences props
  absences?: Absence[];
  contracts?: Contract[];

  // Shifts props
  selectedWorkAreas?: number[];
  weeklyShifts?: any[];
}

function calcDaysExclWeekend(startIso: string, endIso: string): number {
  if (!startIso || !endIso) return 0;
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (end < start) return 0;

  let count = 0;
  let cur = new Date(start);
  while (cur <= end) {
    const wd = cur.getDay();
    if (wd !== 0 && wd !== 6) {
      count++;
    }
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

function isCurrentContract(contract: Contract): boolean {
  const today = new Date();
  const vf = new Date(contract.valid_from);
  const vu = contract.valid_until ? new Date(contract.valid_until) : null;

  if (!vu) {
    return vf <= today;
  }
  return (vf <= today && today <= vu);
}

export default function EmployeeSidebar({
  employees = [],
  activeEmployeeId,
  setActiveEmployeeId,
  activeEmployee,
  setActiveEmployee,
  displayMode = 'simple',
  timeTrackings = [],
  timeRangeMode = 'allTime',
  customStart = '',
  customEnd = '',
  plannedHoursMap = {},
  absences = [],
  contracts = [],
  selectedWorkAreas = [],
  weeklyShifts = [],
}: EmployeeSidebarProps): React.ReactElement {

  const { start, end } = computeRange(timeRangeMode, customStart, customEnd);

  // Shifts mode: calculate hours from weekly shifts
  const shiftsHoursMap = useMemo(() => {
    if (displayMode !== 'shifts') return {};

    const newMap: Record<number, number> = {};
    weeklyShifts.forEach((dayObj: any) => {
      if (!dayObj.shifts) return;
      dayObj.shifts.forEach((sh: any) => {
        const start = new Date(sh.start_time);
        const end = new Date(sh.end_time);
        const diffH = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
        if (sh.assigned_employees) {
          sh.assigned_employees.forEach((aEmp: any) => {
            const empId = aEmp.id;
            if (!newMap[empId]) newMap[empId] = 0;
            newMap[empId] += diffH;
          });
        }
      });
    });
    return newMap;
  }, [weeklyShifts, displayMode]);

  // Shifts mode: count applications (total + weekend) per employee across the week
  const shiftsAppStatsMap = useMemo(() => {
    if (displayMode !== 'shifts') return {};

    const newMap: Record<number, { total: number; weekend: number }> = {};
    weeklyShifts.forEach((dayObj: any) => {
      if (!dayObj.shifts) return;
      dayObj.shifts.forEach((sh: any) => {
        const localDate = dateUtil.convertUTCToTenantTime(sh.start_time) || new Date(sh.start_time);
        const day = localDate.getDay();
        const isWeekend = day === 6 || day === 0 || (day === 5 && localDate.getHours() >= 17);
        (sh.applications || []).forEach((app: any) => {
          const empId = Number(app.employee);
          if (!newMap[empId]) newMap[empId] = { total: 0, weekend: 0 };
          newMap[empId].total += 1;
          if (isWeekend) newMap[empId].weekend += 1;
        });
      });
    });
    return newMap;
  }, [weeklyShifts, displayMode]);

  const empHoursMap = useMemo(() => {
    if (displayMode !== 'timeTracking') return {};
    
    const newMap: Record<number, number> = {};
    timeTrackings.forEach((tt) => {
      if (!tt.start_time || !tt.end_time) return;

      const st = new Date(tt.start_time);
      if (st < start || st >= end) return;

      const et = new Date(tt.end_time);
      let ms = et.getTime() - st.getTime();
      
      if (tt.break_start && tt.break_end) {
        const bs = new Date(tt.break_start);
        const be = new Date(tt.break_end);
        if (be > bs) ms -= (be.getTime() - bs.getTime());
      }
      const empId = (tt as any).employee;
      if (!empId) return;
      if (!newMap[empId]) newMap[empId] = 0;
      newMap[empId] += (ms / 3600000);
    });
    return newMap;
  }, [timeTrackings, start, end, displayMode]);

  const handleEmployeeClick = (emp: Employee): void => {
    const isActive = (activeEmployeeId === emp.id);

    if (displayMode === 'availability' || displayMode === 'absences' || displayMode === 'shifts') {
      if (setActiveEmployeeId && setActiveEmployee) {
        setActiveEmployeeId(isActive ? null : emp.id);
        setActiveEmployee(isActive ? null : emp);
      }
    } else {
      if (setActiveEmployeeId) {
        setActiveEmployeeId(isActive ? null : emp.id);
      }
    }
  };

  // Status: 'ok' = green, 'warning' = orange (within tolerance), 'over' = red
  type HoursStatus = 'ok' | 'warning' | 'over';
  const TOLERANCE_HOURS = 3;

  const getDisplayData = (emp: Employee): { value: string; status: HoursStatus; unit: string } | null => {
    switch (displayMode) {
      case 'timeTracking': {
        const rawHours = empHoursMap[emp.id] || 0;
        const decHours = rawHours.toFixed(1);
        const planned = plannedHoursMap[emp.id] || 0;
        const plannedStr = planned.toFixed(1);
        let status: HoursStatus = 'ok';
        if (rawHours > planned + TOLERANCE_HOURS) {
          status = 'over';
        } else if (rawHours > planned) {
          status = 'warning';
        }
        return { value: `${decHours}/ ${plannedStr}`, status, unit: 'h' };
      }

      case 'shifts': {
        const totalH = shiftsHoursMap[emp.id] || 0;
        const decH = totalH.toFixed(1);
        const weeklyH = (emp as any).contract_hours_per_week || (emp as any).min_hours || 10;
        let status: HoursStatus = 'ok';
        if (totalH > weeklyH + TOLERANCE_HOURS) {
          status = 'over';
        } else if (totalH > weeklyH) {
          status = 'warning';
        }
        return { value: `${decH}/ ${weeklyH}`, status, unit: 'h' };
      }

      case 'absences': {
        const myApproved = absences.filter(a =>
          a.employee === emp.id &&
          a.absence_type === 'HOLIDAY' &&
          (a.status as string).toUpperCase() === 'APPROVED'
        );
        let usedDays = 0;
        myApproved.forEach(a => {
          usedDays += calcDaysExclWeekend(a.start_datetime, a.end_datetime);
        });

        const empAny = emp as any;
        const annualLeave = Number(empAny.annual_leave_days) || 24;

        const status: HoursStatus = usedDays > annualLeave ? 'over' : 'ok';
        return { value: `${usedDays}/ ${annualLeave}`, status, unit: 'd' };
      }

      case 'availability':
      case 'simple':
      default:
        return null;
    }
  };

  const getStatusColors = (status: HoursStatus) => {
    switch (status) {
      case 'over':
        return { background: 'rgba(239, 68, 68, 0.2)', color: '#991b1b' };
      case 'warning':
        return { background: 'rgba(245, 158, 11, 0.2)', color: '#92400e' };
      case 'ok':
      default:
        return { background: 'rgba(16, 185, 129, 0.2)', color: '#065f46' };
    }
  };

  const isEmployeeActive = (emp: Employee): boolean => {
    return emp.id === (activeEmployeeId ?? activeEmployee?.id);
  };

  // Filter employees by work areas (for shifts mode)
  const filteredEmployees = useMemo(() => {
    if (displayMode !== 'shifts' || selectedWorkAreas.length === 0) {
      return employees;
    }

    return employees.filter(emp => {
      if (!(emp as any).work_areas) return false;
      const overlap = (emp as any).work_areas.filter((waId: number) =>
        selectedWorkAreas.includes(waId)
      );
      return overlap.length > 0;
    });
  }, [employees, selectedWorkAreas, displayMode]);

  const activeEmps = useMemo(() =>
    filteredEmployees
      .filter(emp => (emp as any).onboarding_status !== 'DEACTIVATED')
      .sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '')),
    [filteredEmployees]
  );

  const deactivatedEmps = useMemo(() =>
    filteredEmployees
      .filter(emp => (emp as any).onboarding_status === 'DEACTIVATED')
      .sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '')),
    [filteredEmployees]
  );

  const activeEmpIsDeactivated = useMemo(() => {
    if (activeEmployeeId == null && activeEmployee == null) return false;
    const currentId = activeEmployeeId ?? activeEmployee?.id;
    return deactivatedEmps.some(emp => emp.id === currentId);
  }, [activeEmployeeId, activeEmployee, deactivatedEmps]);

  const activeIdInList = useMemo(() => {
    const currentId = activeEmployeeId ?? activeEmployee?.id;
    if (currentId == null) return false;
    return filteredEmployees.some(emp => emp.id === currentId);
  }, [activeEmployeeId, activeEmployee, filteredEmployees]);

  const [showDeactivated, setShowDeactivated] = useState(false);

  useEffect(() => {
    if (activeEmpIsDeactivated) {
      setShowDeactivated(true);
    }
  }, [activeEmpIsDeactivated]);

  const renderAvatar = (emp: Employee, isActive: boolean, isDeactivated?: boolean) => {
    return (
      <EmployeeAvatar
        firstName={emp.first_name}
        lastName={emp.last_name}
        photoUrl={(emp as any).photo_url}
        size={32}
        style={{
          border: isActive ? '2px solid #0e7490' : '2px solid transparent',
          boxShadow: isActive ? '0 0 0 2px rgba(14, 116, 144, 0.2)' : 'none',
          transition: 'border 0.15s ease, box-shadow 0.15s ease',
          filter: isDeactivated ? 'grayscale(100%)' : 'none',
        }}
      />
    );
  };

  const renderEmployeeCard = (emp: Employee, isDeactivated?: boolean) => {
    const isActive = isEmployeeActive(emp);
    const displayData = getDisplayData(emp);

    const lastInit = emp.last_name ? ` ${emp.last_name[0]}.` : '';
    const displayName = (emp.first_name || '') + lastInit;

    let appliedBadge: React.ReactNode = null;
    if (displayMode === 'shifts') {
      const appStat = shiftsAppStatsMap[emp.id];
      const totalApps = appStat?.total || 0;
      const weekendApps = appStat?.weekend || 0;
      const appStatus: HoursStatus =
        totalApps === 0
          ? 'over'
          : totalApps >= 4 && weekendApps >= 1
            ? 'ok'
            : 'warning';
      const appColors = getStatusColors(appStatus);
      appliedBadge = (
        <div
          title={`${totalApps} application${totalApps === 1 ? '' : 's'} total · ${weekendApps} on weekends (Fri eve, Sat, Sun)`}
          style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '3px' }}
        >
          <span style={{
            fontSize: '7px',
            fontWeight: 700,
            color: '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
          }}>
            Apps
          </span>
          <span
            style={{
              fontSize: '8px',
              fontWeight: 700,
              padding: '1px 5px',
              borderRadius: '4px',
              color: appColors.color,
              background: appColors.background,
            }}
          >
            {totalApps}/ {weekendApps}
          </span>
        </div>
      );
    }

    let opacity = isDeactivated ? 0.6 : 1;
    if (!isActive && activeIdInList) {
      opacity = isDeactivated ? 0.4 : 0.5;
    }

    return (
      <div
        key={emp.id}
        data-testid={`employee-${emp.id}`}
        data-active={isActive}
        onClick={() => handleEmployeeClick(emp)}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.08)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive) {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
          }
        }}
        style={{
          cursor: 'pointer',
          padding: '8px 10px',
          borderRadius: '10px',
          border: isActive ? '1px solid rgba(14, 116, 144, 0.4)' : '1px solid rgba(226, 232, 240, 0.8)',
          background: isActive ? 'rgba(14, 116, 144, 0.12)' : 'rgba(255, 255, 255, 0.95)',
          boxShadow: isActive
            ? '0 4px 15px rgba(14, 116, 144, 0.2), 0 0 10px rgba(14, 116, 144, 0.1)'
            : '0 2px 8px rgba(0, 0, 0, 0.04)',
          opacity: opacity,
          transition: 'all 0.15s ease',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        {renderAvatar(emp, isActive, isDeactivated)}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginBottom: displayData ? '3px' : 0,
          }}>
            <span style={{
              flex: 1,
              minWidth: 0,
              fontSize: '11px',
              fontWeight: 600,
              lineHeight: 1.2,
              color: '#1e293b',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {displayName}
            </span>
            {appliedBadge}
          </div>
          {displayData && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div
                style={{
                  flex: 1,
                  height: '4px',
                  background: 'rgba(0, 0, 0, 0.08)',
                  borderRadius: '2px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${Math.min(100, parseFloat(displayData.value.split('/')[0]) / parseFloat(displayData.value.split('/')[1]) * 100)}%`,
                    height: '100%',
                    background: displayData.status === 'over' ? '#ef4444' : displayData.status === 'warning' ? '#f59e0b' : '#10b981',
                    borderRadius: '2px',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
              <span style={{
                fontSize: '9px',
                fontWeight: 600,
                color: displayData.status === 'over' ? '#ef4444' : '#64748b',
                whiteSpace: 'nowrap',
              }}>
                {displayData.value}{displayData.unit}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        width: '100%',
        backgroundColor: 'transparent',
        height: '100%',
        overflow: 'auto',
        padding: '12px',
        boxSizing: 'border-box',
        fontSize: '0.85rem',
      }}
    >
      <h3 style={{
        margin: '0 0 10px 0',
        color: '#0e7490',
        fontSize: '12px',
        fontWeight: 600,
        flexShrink: 0,
      }}>
        Employees
      </h3>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '5px',
        }}
      >
        {activeEmps.map(emp => renderEmployeeCard(emp, false))}
      </div>

      {deactivatedEmps.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          <button
            onClick={() => setShowDeactivated(prev => !prev)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              width: '100%',
              padding: '6px 8px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: 600,
              color: '#94a3b8',
              transition: 'color 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#64748b'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; }}
          >
            <span style={{
              display: 'inline-block',
              transition: 'transform 0.2s ease',
              transform: showDeactivated ? 'rotate(90deg)' : 'rotate(0deg)',
              fontSize: '10px',
            }}>
              ▶
            </span>
            Deactivated ({deactivatedEmps.length})
          </button>
          {showDeactivated && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '5px' }}>
              {deactivatedEmps.map(emp => renderEmployeeCard(emp, true))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
